<main class="access-page">
  <header class="page-header">
    <div>
      <p class="eyebrow">Administration</p>
      <h1>Access control</h1>
      <p class="page-description">Add people to the access role using their GM identity.</p>
    </div>
    <div class="role-pill" aria-label="Role being assigned">
      <span class="role-dot"></span>
      access role
    </div>
  </header>

  <section class="access-layout">
    <article class="search-panel">
      <div class="section-heading">
        <div>
          <h2>Find people</h2>
          <p>Search Graph by organization, display name, GMID, or email.</p>
        </div>
        <span class="selection-count" *ngIf="selectedPeople.size">{{ selectedPeople.size }} selected</span>
      </div>

      <div class="search-controls">
        <label class="field-label" for="people-search-field">Search by</label>
        <select id="people-search-field" [(ngModel)]="searchField" (ngModelChange)="onSearchFieldChange()">
          <option *ngFor="let field of searchFields" [ngValue]="field.value">{{ field.label }}</option>
        </select>
        <label class="field-label search-label" for="people-search">Search</label>
        <div class="search-input-wrap">
          <mat-icon aria-hidden="true">search</mat-icon>
          <input
            id="people-search"
            type="search"
            [value]="searchTerm"
            (input)="onSearchTermChange($any($event.target).value)"
            placeholder="Enter at least 2 characters"
            autocomplete="off" />
          <mat-progress-spinner *ngIf="searching" diameter="20" mode="indeterminate"></mat-progress-spinner>
        </div>
      </div>

      <p class="field-hint">Results are matched against Microsoft Graph and identified by GMID.</p>
      <div class="message error-message" *ngIf="searchError">{{ searchError }}</div>

      <div class="results" *ngIf="searchResults.length; else noResults">
        <div class="result-row" *ngFor="let person of searchResults; trackBy: trackByGmid">
          <label class="result-select" [class.disabled]="isAlreadyAdded(getGmid(person))">
            <input
              type="checkbox"
              [checked]="isSelected(person)"
              [disabled]="isAlreadyAdded(getGmid(person))"
              (change)="togglePerson(person)" />
            <span class="checkmark" aria-hidden="true"></span>
          </label>
          <div class="person-avatar">
            <img *ngIf="photoForGmid(getGmid(person)) as photo" [src]="photo" alt="" />
            <span *ngIf="!photoForGmid(getGmid(person))">{{ (person.displayName || getGmid(person)).charAt(0).toUpperCase() }}</span>
          </div>
          <div class="person-details">
            <strong>{{ person.displayName || 'Unnamed person' }}</strong>
            <span>{{ getEmail(person) }}</span>
            <span class="person-meta">
              <span>{{ getGmid(person) || 'GMID unavailable' }}</span>
              <span *ngIf="person.department || person.companyName">{{ person.department || person.companyName }}</span>
            </span>
          </div>
          <span class="already-added" *ngIf="isAlreadyAdded(getGmid(person))">Already added</span>
        </div>
      </div>

      <ng-template #noResults>
        <div class="empty-state" *ngIf="searchTerm.trim().length < 2">Start typing to search for a person.</div>
        <div class="empty-state" *ngIf="searchTerm.trim().length >= 2 && !searching && !searchError">No people found.</div>
      </ng-template>

      <div class="selection-footer" *ngIf="selectedPeople.size">
        <span>{{ selectedPeople.size }} user{{ selectedPeople.size === 1 ? '' : 's' }} ready to add</span>
        <button type="button" class="primary-button" (click)="addSelectedUsers()" [disabled]="saving">
          <mat-progress-spinner *ngIf="saving" diameter="18" mode="indeterminate"></mat-progress-spinner>
          <mat-icon *ngIf="!saving" aria-hidden="true">person_add</mat-icon>
          {{ saving ? 'Adding...' : 'Add users' }}
        </button>
      </div>
      <div class="message success-message" *ngIf="saveMessage">{{ saveMessage }}</div>
      <div class="message error-message" *ngIf="accessError && selectedPeople.size">{{ accessError }}</div>
    </article>

    <article class="access-list-panel">
      <div class="section-heading">
        <div>
          <h2>People with access</h2>
          <p>{{ accessUsers.length }} assigned user{{ accessUsers.length === 1 ? '' : 's' }}</p>
        </div>
        <mat-progress-spinner *ngIf="loadingAccessUsers" diameter="22" mode="indeterminate"></mat-progress-spinner>
      </div>

      <div class="message error-message" *ngIf="accessError && !selectedPeople.size">{{ accessError }}</div>
      <div class="access-list" *ngIf="accessUsers.length; else noAccessUsers">
        <div class="access-row" *ngFor="let user of accessUsers; trackBy: trackByAccessId">
          <div class="person-avatar small">
            <img *ngIf="photoForGmid(user.gmid) as photo" [src]="photo" alt="" />
            <span *ngIf="!photoForGmid(user.gmid)">{{ user.gmid.charAt(0).toUpperCase() }}</span>
          </div>
          <div class="person-details">
            <strong>{{ user.gmid }}</strong>
            <span>{{ user.email || 'No email available' }}</span>
            <span class="person-meta">{{ user.gmid }}</span>
          </div>
          <div class="access-actions">
            <label class="sr-only" [for]="'role-' + user.id">Role for {{ user.gmid }}</label>
            <input
              class="role-input"
              [id]="'role-' + user.id"
              [ngModel]="getRoleDraft(user)"
              (ngModelChange)="setRoleDraft(user, $event)"
              [disabled]="isUpdatingUser(user)" />
            <button
              *ngIf="isRoleChanged(user)"
              type="button"
              class="row-button"
              (click)="saveUserRole(user)"
              [disabled]="isUpdatingUser(user) || !getRoleDraft(user)">
              Save role
            </button>
            <button
              type="button"
              class="row-button"
              [class.destructive]="!!user.isActive"
              (click)="setUserActive(user, !user.isActive)"
              [disabled]="isUpdatingUser(user)">
              {{ isUpdatingUser(user) ? 'Saving...' : user.isActive ? 'Deactivate' : 'Reactivate' }}
            </button>
            <span class="status-badge" [class.inactive]="!user.isActive">
              {{ user.isActive ? 'Active' : 'Inactive' }}
            </span>
          </div>
        </div>
      </div>
      <ng-template #noAccessUsers>
        <div class="empty-state">No users have been assigned yet.</div>
      </ng-template>
    </article>
  </section>
</main>
